/**
 * Reliability Score Card
 * Shows guest's reliability tier, score, and rewards
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { Trophy, TrendingUp, Star, Gift, Info, Loader2, Award } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';


const TIER_COLORS = {
  Excellent: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-600',
    border: 'border-amber-500/20',
    icon: Trophy
  },
  Good: {
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-600',
    border: 'border-emerald-500/20',
    icon: Star
  },
  Standard: {
    bg: 'bg-slate-500/10',
    text: 'text-slate-600',
    border: 'border-slate-500/20',
    icon: Award
  }
};

export const ReliabilityScoreCard = ({ userEmail }) => {
  const [loading, setLoading] = useState(true);
  const [reliability, setReliability] = useState(null);
  const [tierInfo, setTierInfo] = useState([]);

  useEffect(() => {
    if (userEmail) {
      fetchReliability();
      fetchTierInfo();
    }
  }, [userEmail]);

  const fetchReliability = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/reliability/guest/${userEmail}`);
      setReliability(response.data);
    } catch (error) {
      console.error('Error fetching reliability:', error);
      toast.error('Failed to load reliability data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTierInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reliability/tiers`);
      setTierInfo(response.data.tiers || []);
    } catch (error) {
      console.error('Error fetching tier info:', error);
    }
  };

  if (loading) {
    return (
      <Card data-testid="reliability-score-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            <CardTitle>Reliability Rewards</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!reliability) {
    return null;
  }

  const tierConfig = TIER_COLORS[reliability.reliability_tier] || TIER_COLORS.Standard;
  const TierIcon = tierConfig.icon;
  const currentTierInfo = tierInfo.find(t => t.name === reliability.reliability_tier);
  const discount = currentTierInfo?.discount_percentage || 0;

  return (
    <Card data-testid="reliability-score-card" className="border-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            <CardTitle>Reliability Rewards</CardTitle>
          </div>
          <Badge 
            variant="secondary" 
            className={`${tierConfig.bg} ${tierConfig.text} ${tierConfig.border}`}
            data-testid="reliability-tier-badge"
          >
            <TierIcon className="w-3 h-3 mr-1" />
            {reliability.reliability_tier}
          </Badge>
        </div>
        <CardDescription>
          Earn rewards by being a reliable guest
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Score Display */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div>
            <p className="text-sm font-medium">Your Reliability Score</p>
            <p className="text-xs text-muted-foreground mt-1">
              Based on {reliability.metrics.total_bookings} booking{reliability.metrics.total_bookings !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="text-3xl heading-font font-bold text-primary">
            {reliability.reliability_score}/100
          </div>
        </div>

        {/* Current Rewards */}
        {discount > 0 ? (
          <Alert className="bg-emerald-500/5 border-emerald-500/20">
            <Gift className="w-4 h-4 text-emerald-600" />
            <AlertDescription>
              <p className="font-medium text-emerald-700">🎉 You are saving {discount}% on all bookings!</p>
              <p className="text-xs text-emerald-600 mt-1">
                Keep up your great track record to maintain your rewards.
              </p>
            </AlertDescription>
          </Alert>
        ) : (
          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription className="text-xs">
              Complete more stays and maintain low cancellations to unlock discounts up to 10%!
            </AlertDescription>
          </Alert>
        )}

        <Separator />

        {/* Metrics */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Your Stats</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground">Completed Stays</p>
              <p className="text-lg font-semibold">{reliability.metrics.completed_stays}</p>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground">Cancellation Rate</p>
              <p className="text-lg font-semibold">{reliability.cancellation_rate}%</p>
            </div>
          </div>
        </div>

        {/* ID Verification Boost */}
        {reliability.metrics.id_verified && (
          <Alert className="bg-primary/5 border-primary/20">
            <TrendingUp className="w-4 h-4 text-primary" />
            <AlertDescription className="text-xs">
              <span className="font-medium text-primary">Verified Guest Bonus:</span> +10 points to your score!
            </AlertDescription>
          </Alert>
        )}

        {!reliability.metrics.id_verified && (
          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription className="text-xs">
              💡 Get verified to instantly boost your reliability score by 10 points!
            </AlertDescription>
          </Alert>
        )}

        {/* Next Tier Info */}
        {reliability.reliability_tier !== 'Excellent' && (
          <div className="pt-2">
            <p className="text-xs text-muted-foreground mb-2">Next Tier: {reliability.reliability_tier === 'Standard' ? 'Good' : 'Excellent'}</p>
            <div className="space-y-1">
              {reliability.reliability_tier === 'Standard' && (
                <>
                  <p className="text-xs flex items-center gap-1">
                    • Complete more stays to increase your score to 60+
                  </p>
                  <p className="text-xs flex items-center gap-1">
                    • Keep cancellations below 30%
                  </p>
                </>
              )}
              {reliability.reliability_tier === 'Good' && (
                <>
                  <p className="text-xs flex items-center gap-1">
                    • Increase your score to 80+ (current: {reliability.reliability_score})
                  </p>
                  <p className="text-xs flex items-center gap-1">
                    • Keep cancellations below 10% (current: {reliability.cancellation_rate}%)
                  </p>
                  <p className="text-xs flex items-center gap-1">
                    • Avoid no-shows
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};